<template>
  <div class="groups-list">
    <toolbar
      :title="parentGroupId ? 'Groups' : 'Organizations'"
      @search="search"
    >
      <template v-if="!parentGroupId" slot="checkbox">
        <md-checkbox v-model="state.showTesting">Display Test Orgs</md-checkbox>
      </template>
    </toolbar>
    <md-progress-bar v-if="loading || removing" md-mode="indeterminate" />
    <md-table :value="filteredGroups" md-card @md-selected="onSelect">
      <md-table-row
        slot="md-table-row"
        slot-scope="{item: group}"
        md-selectable="multiple"
        :md-disabled="!parentGroupId || !canEdit(group)"
      >
        <md-table-cell :md-label="parentGroupId ? 'Group Name' : 'Org Name'">
          <group-name :group="group" />
        </md-table-cell>
        <md-table-cell v-if="!parentGroupId" md-label="Status">
          <div
            class="status"
            :class="{active: !group.metadata.inactive, inactive: !!group.metadata.inactive}"
          >
            {{ group.metadata.inactive ? 'Inactive' : 'Active' }}
          </div>
        </md-table-cell>
        <md-table-cell :md-label="parentGroupId ? 'Group Admin' : 'Key Owner'">
          <span v-if="group.owner">
            <span
              v-if="group.owner.given_name && group.owner.family_name"
            >{{ group.owner.given_name }} {{ group.owner.family_name }}</span>
            <span v-else>{{ group.owner.email }}</span>
          </span>
        </md-table-cell>
        <md-table-cell v-if="!parentGroupId" md-label="Product Type">
          <span>
            {{ productType(group) }}
          </span>
        </md-table-cell>
        <md-table-cell md-label="# of Users">
          <span
            class="seats"
            :class="{exceeded: parentGroupId ? false : (group.metadata.memberCount) > group.metadata.seats}"
          >
            {{ (group.metadata.memberCount || group.members.length || 0) }}
            {{ group.metadata.seats ? `/${group.metadata.seats}` : '' }}
          </span>
        </md-table-cell>
        <md-table-cell md-label="Actions">
          <md-menu :md-offset-x="-150" :md-offset-y="-36">
            <md-button class="md-icon-button" md-menu-trigger>
              <md-icon>more_vert</md-icon>
            </md-button>

            <md-menu-content>
              <router-link
                :to="{name: parentGroupId ? 'group-details' : 'organization-details', params: {orgId: group._id}}"
              >
                <md-menu-item v-if="!parentGroupId">Edit Organization</md-menu-item>
                <md-menu-item v-if="parentGroupId && canEdit(group)">Edit Group</md-menu-item>
                <md-menu-item v-if="parentGroupId && !canEdit(group)">View Group</md-menu-item>
              </router-link>
              <md-menu-item
                v-if="!parentGroupId"
                @click="openChangeStatusModal(group)"
              >
                Change Status
              </md-menu-item>
              <md-menu-item
                v-if="parentGroupId && canEdit(group)"
                class="md-accent"
                @click="openRemoveModal(group)"
              >
                Remove Group
              </md-menu-item>
            </md-menu-content>
          </md-menu>
        </md-table-cell>
      </md-table-row>
    </md-table>
    <md-toolbar class="md-primary bottom-toolbar new-globals">
      <!-- TODO: Check with Trent -->
      <md-button
        v-if="parentGroupId"
        class="md-raised md-accent"
        @click="openRemoveModal()"
      >
        Remove Group(s)
      </md-button>
      <md-button
        class="md-raised md-secondary"
        @click="openInviteModal"
      >
        {{ parentGroupId ? 'Create New Group' : 'Invite Key Owner' }}
      </md-button>
    </md-toolbar>
    <md-dialog :md-active.sync="state.inviteModalOpen" class="new-globals" :md-fullscreen="false">
      <md-progress-bar v-if="inviting" md-mode="indeterminate" />
      <md-dialog-title>
        {{ parentGroupId ? 'Create New Group' : 'Invite Key Owner' }}
        <br>
        <span class="md-caption">Select a text field to modify its content</span>
      </md-dialog-title>

      <md-dialog-content>
        <span v-if="error">{{ error.message || error.error || error }}</span>
        <form @submit.prevent="submitInvite">
          <md-field v-if="!parentGroupId">
            <label>Email</label>
            <md-input
              name="email"
              placeholder="Email"
              :value="state.user.email"
              @input="value => updateUserEmail(value.trim())"
            />
          </md-field>
          <md-field>
            <label>{{ parentGroupId ? 'Group Name' : 'Organization Name' }}</label>
            <md-input
              name="groupName"
              :placeholder="parentGroupId ? 'Group Name' : 'Organization Name'"
              :value="state.group.name"
              :maxlength="40"
              @input="value => updateGroupName(value.trim())"
            />
          </md-field>
          <md-field v-if="!parentGroupId">
            <label>Product Type</label>
            <md-select v-model="state.group.metadata.productType">
              <md-option value="EMS">EMS</md-option>
              <md-option value="NURSING">Nursing</md-option>
              <md-option value="MILITARY">Military</md-option>
            </md-select>
          </md-field>
          <md-field v-if="!parentGroupId">
            <label># of Seats</label>
            <md-input
              type="number"
              name="groupSeats"
              placeholder="# of Seats"
              :value="state.group.metadata.seats"
              @input="value => updateGroupSeats(parseInt(value))"
            />
          </md-field>
          <md-checkbox
            v-if="!parentGroupId"
            v-model="state.group.metadata.dev"
            name="dev"
          >
            Internal VES Org
          </md-checkbox>
        </form>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-primary" @click="closeInviteModal()">Cancel</md-button>
        <md-button
          :disabled="!state.group.name"
          class="md-primary"
          @click="submitInvite()"
        >
          {{ parentGroupId ? 'Confirm' : 'Submit' }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog-confirm
      :md-active.sync="state.changeStatusModalOpen"
      md-title="Change organization status?"
      md-content="Inactive organizations do not have access to cases."
      md-confirm-text="Confirm"
      md-cancel-text="Cancel"
      @md-confirm="updateGroupStatus()"
    />

    <md-dialog-confirm
      :md-active.sync="state.removeModalOpen"
      md-title="Remove groups?"
      md-content="This action is permanent and can't be reverted."
      md-confirm-text="Confirm"
      md-cancel-text="Cancel"
      @md-confirm="removeSelectedGroups()"
    />
  </div>
</template>

<script>
import isNumber from 'lodash/isNumber';
import debounce from 'lodash/debounce';

import Toolbar from '../../../../lib/common/components/Toolbar';
import GroupName from '../../common/GroupName';

export default {
  name: 'groups-list',
  components: {
    Toolbar,
    GroupName
  },
  props: {
    parentGroupId: {
      type: String,
      default: null
    },
    user: {
      type: Object,
      required: true
    },
    groups: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    inviting: {
      type: Boolean,
      default: false
    },
    updating: {
      type: Boolean,
      default: false
    },
    removing: {
      type: Boolean,
      default: false
    },
    error: {
      type: Object,
      default: null
    },
  },
  data() {
    return {
      state: {
        inviteModalOpen: false,
        changeStatusModalOpen: false,
        removeModalOpen: false,
        selectedGroup: null,
        selectedGroups: [],
        user: {
          email: '',
        },
        group: {
          name: '',
          metadata: {
            seats: 0,
            productType: 'EMS',
          },
        },
        showTesting: false,
      }
    };
  },
  computed: {
    filteredGroups() {
      return this.groups.filter(group => {
        return this.state.showTesting || !group.metadata.testing;
      });
    },
  },
  watch: {
    inviting(newVal, oldVal) {
      if (oldVal && !newVal && !this.error) {
        this.resetInviteForm();
        this.closeInviteModal();
      }
    }
  },
  methods: {
    isCurrentUserKeyOwner(group) {
      if (group.metadata.ownerId === this.user.user_id) {
        return true;
      }
      return false;
    },
    canEdit(group) {
      const { user } = this;
      return (
        user.role === 'ADMIN' ||
        user.role === 'SUPER_ADMIN' ||
        this.isCurrentUserKeyOwner(group)
      );
    },
    search: debounce(function search(term) {
      this.$emit('search', term);
    }, 500),
    openInviteModal() {
      this.resetInviteForm();
      this.state.inviteModalOpen = true;
    },
    closeInviteModal() {
      this.state.inviteModalOpen = false;
    },
    submitInvite() {
      if (this.parentGroupId) {
        this.$analytics.event('Org Management', 'create_new_group');
        this.$emit('invite', null, this.state.group);
      } else {
        this.$emit('invite', this.state.user, this.state.group);
      }
    },
    updateUserEmail(email) {
      this.state.user.email = email;
    },
    updateGroupName(name) {
      this.state.group.name = name;
    },
    updateGroupSeats(seats) {
      if (!isNumber(seats)) {
        return;
      }
      this.state.group.metadata.seats = seats;
    },
    onSelect(groups) {
      this.state.selectedGroups = groups;
    },
    openChangeStatusModal(group) {
      this.state.selectedGroup = group;
      this.state.changeStatusModalOpen = true;
    },
    updateGroupStatus() {
      this.$emit('update', this.state.selectedGroup, {
        metadata: { inactive: !this.state.selectedGroup.metadata.inactive }
      });
    },
    openRemoveModal(group) {
      if (group) {
        this.state.selectedGroups = [group];
      }
      this.state.removeModalOpen = true;
      if (this.parentGroupId) {
        this.$analytics.event('Org Management', 'remove_group', undefined, this.state.selectedGroups.length);
      }
    },
    closeRemoveModal() {
      this.state.removeModalOpen = false;
    },
    removeSelectedGroups() {
      if (!this.state.selectedGroups || !this.state.selectedGroups.length) {
        return;
      }
      this.$emit('remove', this.state.selectedGroups);
    },
    resetInviteForm() {
      this.$emit('clearError');
      this.state.user = { name: '' };
      this.state.group = {
        name: '',
        metadata: {
          seats: 0,
          productType: 'EMS',
        },
      };
    },
    productType(group) {
      return ({
        EMS: 'Emergency',
        NURSING: 'Nursing',
        MILITARY: 'Military',
      })[group.metadata.productType] || 'Emergency';
    },
  }
};
</script>

<style scoped>
.groups-list {
  height: 100%;
}
.md-table {
  /* Height of container minus top and bottom toolbar */
  height: calc(100% - 64px - 64px);
}
.status {
  border: solid 1px;
  border-radius: 3px;
  font-weight: bold;
  height: 30px;
  width: 82px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status.active {
  border-color: #3d9e51;
  color: #3d9e51;
}

.status.inactive {
  border-color: #cd6262;
  color: #cd6262;
}

.seats.exceeded {
  color: #cd6262;
  font-weight: bold;
}

.md-table {
  margin: 0;
}

.bottom-toolbar {
  display: flex;
  justify-content: flex-end;
}
</style>